import SectionDivider from '@components/profile/teacher/section-divider'
import { useProfileContext } from '@contexts/profile'
import React, { FC, SVGAttributes } from 'react'
import {
  Award,
  Calendar,
  Clock,
  Globe,
  Star,
  Users,
  Video,
} from 'react-feather'

// Matches react-feather's exported Icon type. Their declarations don't expose
// the type by name, so we redeclare its surface here.
type FeatherIcon = FC<
  SVGAttributes<SVGElement> & { color?: string; size?: string | number }
>

type StatCardProps = {
  title: string
  value: string | number
  Icon: FeatherIcon
  iconBg: string
  iconColor: string
}

const StatCard = ({ title, value, Icon, iconBg, iconColor }: StatCardProps) => (
  <div className="flex items-center p-4 rounded-lg border border-grey-200">
    <div
      className={`flex items-center justify-center w-14 h-14 rounded-full ${iconBg} flex-shrink-0`}
    >
      <Icon size={26} className={iconColor} />
    </div>
    <div className="ml-4 leading-tight">
      <p className="text-grey-900 font-600 text-base">{title}</p>
      <p className="text-grey-900 text-2xl font-700 mt-1">{value}</p>
    </div>
  </div>
)

// Show punctuality only when there's enough signal to be meaningful — under
// ~20 classes the percentage is too sensitive to a single late join.
const MIN_CLASSES_FOR_PUNCTUALITY_DISPLAY = 20

const TeacherStats = () => {
  const { profileDetails } = useProfileContext()
  const stats = profileDetails?.teacher?.stats
  if (!stats) return null

  const cards: StatCardProps[] = []

  if (stats.yearsOnPlatform > 0) {
    cards.push({
      title: 'Years on Codingal',
      value: stats.yearsOnPlatform,
      Icon: Calendar,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-300',
    })
  }
  if (stats.totalClassesTaught > 0) {
    cards.push({
      title: 'Classes taught',
      value: stats.totalClassesTaught.toLocaleString(),
      Icon: Video,
      iconBg: 'bg-orange2-50',
      iconColor: 'text-orange',
    })
  }
  if (stats.uniqueStudentsTaught > 0) {
    cards.push({
      title: 'Students taught',
      value: stats.uniqueStudentsTaught.toLocaleString(),
      Icon: Users,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-300',
    })
  }
  if (stats.totalRatings > 0) {
    cards.push({
      title: `Avg rating (${stats.totalRatings} reviews)`,
      value: `${stats.avgRating.toFixed(1)} / 5`,
      Icon: Star,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-200',
    })
  }
  if (
    stats.lifetimeClassesForPunctuality >= MIN_CLASSES_FOR_PUNCTUALITY_DISPLAY
  ) {
    cards.push({
      title: 'On-time joining',
      value: `${stats.lifetimePunctualityPercentage.toFixed(1)}%`,
      Icon: Clock,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-100',
    })
  }
  if (stats.countriesTaughtCount > 0) {
    cards.push({
      title:
        stats.countriesTaughtCount === 1
          ? 'Country reached'
          : 'Countries reached',
      value: stats.countriesTaughtCount.toLocaleString(),
      Icon: Globe,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-200',
    })
  }
  // If we have no real stats yet, fall back to a generic credential card so
  // the row never feels empty.
  if (cards.length === 0) {
    cards.push({
      title: 'Verified instructor',
      value: 'Codingal',
      Icon: Award,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-100',
    })
  }

  // Layout: 2 cols on small, up to 3 on lg (cleaner with 5–6 stats than 4-up)
  const gridCols =
    cards.length >= 3 ? 'lg:grid-cols-3' : `lg:grid-cols-${cards.length}`

  return (
    <>
      <SectionDivider />
      <section className="p-5 lg:p-10">
        <div className={`grid gap-4 md:grid-cols-2 ${gridCols}`}>
          {cards.map((card) => (
            <StatCard key={card.title} {...card} />
          ))}
        </div>
      </section>
    </>
  )
}

export default React.memo(TeacherStats)
